import { Factory, Tags, Target, Truck, type LucideIcon } from 'lucide-react'
import { z } from 'zod'
import { defaultForecastRange } from '../previsions-produits/-search-schema'

export const MEASURES = ['uvc', 'ca', 'tonnage'] as const
export const SEGMENTS = ['uf', 'commercial', 'industriel', 'reappro'] as const

export type Measure = (typeof MEASURES)[number]
export type Segment = (typeof SEGMENTS)[number]

export const PREVISIONS_VIEWS = ['ventes', 'production', 'logistique'] as const
const PROJECTION_MEASURES = ['tonnage', 'uvc', 'caisses', 'palis'] as const

export type PrevisionsView = (typeof PREVISIONS_VIEWS)[number]
export type ProjectionView = Exclude<PrevisionsView, 'ventes'>
export type ProjectionMeasure = (typeof PROJECTION_MEASURES)[number]

/** Mesures du graphique de chaque vue projection, la première étant celle par défaut. */
export const PROJECTION_VIEW_MEASURES: Record<ProjectionView, readonly ProjectionMeasure[]> = {
  production: ['tonnage', 'uvc'],
  logistique: ['uvc', 'caisses', 'palis'],
}

export const resolveProjectionMeasure = (
  view: ProjectionView,
  measure: ProjectionMeasure | undefined,
): ProjectionMeasure => {
  const measures = PROJECTION_VIEW_MEASURES[view]
  return measure !== undefined && measures.includes(measure) ? measure : measures[0]
}

export const previsionsSearchSchema = z.object({
  storeIds: z.array(z.number().int().positive()).optional(),
  startDate: z.iso.date().optional().catch(undefined),
  endDate: z.iso.date().optional().catch(undefined),
  categoryIds: z.array(z.number().int().nonnegative()).optional().default([]),
  kitchenIds: z.array(z.number().int().positive()).optional().default([]),
  measure: z.enum(MEASURES).optional().default('uvc'),
  segment: z.enum(SEGMENTS).optional().default('commercial'),
  capping: z.boolean().catch(true),
  includeProjects: z.boolean().optional().default(false),
  projectIds: z.array(z.number().int().positive()).optional(),
  focusDate: z.iso.date().optional().catch(undefined),
  view: z.enum(PREVISIONS_VIEWS).catch('ventes'),
  projectionMeasure: z.enum(PROJECTION_MEASURES).optional().catch(undefined),
  reapproCategoryIds: z.array(z.number().int().nonnegative()).optional().default([]),
})

export type PrevisionsSearch = z.infer<typeof previsionsSearchSchema>

export interface PrevisionsRange {
  start: string
  end: string
}

export const resolvePrevisionsRange = (search: PrevisionsSearch): PrevisionsRange => {
  const fallback = defaultForecastRange()
  return {
    start: search.startDate ?? fallback.start,
    end: search.endDate ?? fallback.end,
  }
}

export const selectedStoreIdsOf = (
  search: Pick<PrevisionsSearch, 'storeIds' | 'includeProjects'>,
  allStoreIds: readonly number[],
): number[] => {
  const { storeIds } = search
  // Avant la sélection explicite, `[]` voulait dire « toutes » : les anciens liens le portent encore.
  if (storeIds === undefined || (storeIds.length === 0 && !search.includeProjects)) {
    return [...allStoreIds]
  }
  return storeIds
}

export const selectedProjectIdsOf = (
  search: Pick<PrevisionsSearch, 'projectIds' | 'includeProjects'>,
  allProjectIds: readonly number[],
): number[] => (search.includeProjects ? (search.projectIds ?? [...allProjectIds]) : [])

export const isDateInRange = (date: string, range: PrevisionsRange): boolean =>
  date >= range.start && date <= range.end

export const MEASURE_LABELS: Record<Measure | ProjectionMeasure, string> = {
  uvc: 'UVC',
  ca: 'CA',
  tonnage: 'Tonnage',
  caisses: 'Caisses',
  palis: 'Palis',
}

export const SEGMENT_LABELS: Record<Segment, string> = {
  uf: 'UF / Negoce',
  commercial: 'Commercial',
  industriel: 'Industriel',
  reappro: 'Réappro',
}

export const SEGMENT_ICONS: Record<Segment, LucideIcon> = {
  uf: Target,
  commercial: Tags,
  industriel: Factory,
  reappro: Truck,
}

export type CategorySource = 'commercial' | 'internal' | 'reappro'

export const SEGMENT_CATEGORY_SOURCE: Record<Segment, CategorySource> = {
  uf: 'commercial',
  commercial: 'commercial',
  industriel: 'internal',
  reappro: 'reappro',
}

/** Axe de regroupement attendu par l'endpoint des séries journalières. */
export const SEGMENT_GROUP_BY: Record<Segment, 'uf' | 'category' | 'internal' | 'reappro'> = {
  uf: 'uf',
  commercial: 'category',
  industriel: 'internal',
  reappro: 'reappro',
}

export type AxisGroupBy = (typeof SEGMENT_GROUP_BY)[Segment]

export const PREVISIONS_EXPORT_GROUP_BYS: AxisGroupBy[] = SEGMENTS.map(
  (segment) => SEGMENT_GROUP_BY[segment],
)
